import { useState } from "react";
import { Link as LinkIcon, FileDown } from "lucide-react";
import { useMatches, useNavigate } from "@tanstack/react-router";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useConceptList } from "@/hooks/useConcepts";
import {
  useMarkEventsViewed,
  useZoteroSyncEvents,
} from "@/hooks/useZoteroSyncEvents";
import {
  groupEventsForBell,
  lookupConceptName,
} from "@/lib/zoteroSyncBellGrouping";
import type { ZoteroSyncEvent } from "@/types/zoteroSync";

import { UnmatchedView } from "./UnmatchedView";
import { ZoteroSyncPeekDialog } from "./ZoteroSyncPeekDialog";

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function ZoteroSyncBell() {
  const { recent, unreadCount, unmatchedCount } = useZoteroSyncEvents();
  const { data: conceptList } = useConceptList();
  const concepts = conceptList ?? [];
  const markViewed = useMarkEventsViewed();
  const navigate = useNavigate();
  const matches = useMatches();
  const [open, setOpen] = useState(false);
  const [peekEvent, setPeekEvent] = useState<ZoteroSyncEvent | null>(null);
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);

  const conceptMatch = matches.find(
    (m) => (m.params as Record<string, unknown>).conceptId != null,
  );
  const currentConceptId = conceptMatch
    ? (conceptMatch.params as { conceptId: string }).conceptId
    : null;

  const grouped = groupEventsForBell(recent, currentConceptId, concepts);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && recent.length > 0) {
      const unviewed = recent.filter((e) => !e.viewed_at).map((e) => e.id);
      if (unviewed.length > 0) markViewed.mutate(unviewed);
    }
  };

  function handleRowClick(e: ZoteroSyncEvent) {
    if (!e.concept_id || !e.paper_id) return;
    if (e.concept_id === currentConceptId) {
      // Merge into existing search so wsId / filters / scope survive.
      navigate({
        to: "/concepts/$conceptId/curate" as never,
        params: { conceptId: e.concept_id } as never,
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          selectedPaperId: e.paper_id,
        })) as never,
      });
      setOpen(false);
    } else {
      setPeekEvent(e);
      setOpen(false);
    }
  }

  const peekConceptName = lookupConceptName(peekEvent?.concept_id, concepts);

  return (
    <>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="relative rounded p-1 transition-colors hover:bg-muted"
            aria-label="Zotero sync"
          >
            <FileDown className="size-4" />
            {unreadCount > 0 && (
              <span
                data-testid="zotero-sync-bell-badge"
                className="absolute right-0 top-0 size-2 rounded-full bg-violet-500"
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={6}
          className="w-[28rem] p-0 text-sm"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <span className="text-[13px] font-semibold">Zotero sync</span>
            <span className="font-mono text-[10.5px] text-muted-foreground">
              {unreadCount > 0
                ? `${unreadCount} new`
                : `${recent.length} recent`}
            </span>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {recent.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                No recent attaches.
              </div>
            ) : grouped.kind === "grouped" ? (
              <>
                {grouped.current.length > 0 && (
                  <Section
                    eyebrow={
                      <>
                        In{" "}
                        <span className="font-semibold normal-case tracking-normal text-foreground">
                          {grouped.currentConceptName}
                        </span>
                      </>
                    }
                    count={grouped.current.length}
                  >
                    {grouped.current.map((e) => (
                      <Row
                        key={e.id}
                        event={e}
                        showConcept={false}
                        conceptName=""
                        onClick={() => handleRowClick(e)}
                      />
                    ))}
                  </Section>
                )}
                {grouped.others.length > 0 && (
                  <Section
                    eyebrow="Other concepts"
                    count={grouped.others.length}
                  >
                    {grouped.others.map((e) => (
                      <Row
                        key={e.id}
                        event={e}
                        showConcept
                        conceptName={lookupConceptName(e.concept_id, concepts)}
                        onClick={() => handleRowClick(e)}
                      />
                    ))}
                  </Section>
                )}
              </>
            ) : (
              <Section eyebrow={null} count={null}>
                {grouped.events.map((e) => (
                  <Row
                    key={e.id}
                    event={e}
                    showConcept
                    conceptName={lookupConceptName(e.concept_id, concepts)}
                    onClick={() => handleRowClick(e)}
                  />
                ))}
              </Section>
            )}
          </div>

          <div className="border-t border-border p-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setUnmatchedOpen(true);
              }}
              className="flex w-full items-center justify-between rounded px-3 py-2 text-[12.5px] font-medium hover:bg-muted"
            >
              <span className="flex items-center gap-2">
                <LinkIcon className="size-3.5" />
                View unmatched
                <span className="rounded-full bg-violet-500 px-1.5 py-px font-mono text-[10px] font-medium text-white">
                  {unmatchedCount}
                </span>
              </span>
              <span aria-hidden className="text-muted-foreground">
                →
              </span>
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <ZoteroSyncPeekDialog
        event={peekEvent}
        conceptName={peekConceptName}
        open={peekEvent != null}
        onOpenChange={(next) => {
          if (!next) setPeekEvent(null);
        }}
      />

      <Dialog open={unmatchedOpen} onOpenChange={setUnmatchedOpen}>
        {/* max-h + inner scroll: 50 unmatched items must not grow the fixed
            dialog past the viewport (off-screen ✕ + scroll-locked page reads
            as an app lock-up). */}
        <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Zotero — unmatched items</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <UnmatchedView />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface SectionProps {
  eyebrow: React.ReactNode | null;
  count: number | null;
  children: React.ReactNode;
}

function Section({ eyebrow, count, children }: SectionProps) {
  return (
    <div className="border-b border-border last:border-0">
      {eyebrow != null && (
        <div className="flex items-center justify-between px-4 pb-1.5 pt-3">
          <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            {eyebrow}
          </span>
          {count != null && (
            <span className="font-mono text-[10.5px] text-muted-foreground">
              {String(count).padStart(2, "0")}
            </span>
          )}
        </div>
      )}
      <div className="px-1.5 pb-1.5">{children}</div>
    </div>
  );
}

interface RowProps {
  event: ZoteroSyncEvent;
  showConcept: boolean;
  conceptName: string;
  onClick: () => void;
}

function Row({ event, showConcept, conceptName, onClick }: RowProps) {
  const title = event.paper_title ?? event.title_hint ?? event.doi ?? "(paper)";
  const unread = !event.viewed_at;
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative grid w-full grid-cols-[1fr_auto] items-start gap-x-2.5 rounded-md px-3 py-2 text-left transition-colors hover:bg-muted"
    >
      <span
        aria-hidden
        className={
          "absolute bottom-2.5 left-1 top-2.5 w-0.5 rounded-sm " +
          (unread ? "bg-violet-500" : "bg-transparent")
        }
      />
      <div className="min-w-0">
        <div
          className={
            "line-clamp-2 text-[13px] leading-snug " +
            (unread ? "font-semibold" : "font-medium")
          }
        >
          {title}
        </div>
        {showConcept && conceptName && (
          <div className="mt-1">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px] font-medium">
              <span className="size-1 rounded-full bg-violet-500" />
              {conceptName}
            </span>
          </div>
        )}
      </div>
      <div className="pt-0.5 font-mono text-[10.5px] text-muted-foreground">
        {relativeTime(event.created_at)}
      </div>
    </button>
  );
}
